import React, { useEffect, useState, useRef } from 'react';
import { getApi, postApi } from '../../services/api';

const Team = () => {
  const [myteam, setMyteam] = useState([]);
  const [levelData, setLevelData] = useState([]);
  const [tabaleView, setTableview] = useState('directTeam');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  // Binary Tree State
  const [rootTree, setRootTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [loadingNodes, setLoadingNodes] = useState({});
  const [selectedUserModal, setSelectedUserModal] = useState(null);
  const [searchUserId, setSearchUserId] = useState('');

  // Hold / Long-press refs
  const holdTimeoutRef = useRef(null);
  const isLongPressRef = useRef(false);

  const getTeamdata = async () => {
    setTableLoading(true);
    try {
      const response = await getApi('my-referral');
      if (response.data?.status === true || response.status === true) {
        setMyteam(response.data?.data || response.data);
      } else {
        setMyteam([]);
      }
      setTableview('directTeam');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    getTeamdata();
  }, []);

  const getlevelData = async () => {
    setTableLoading(true);
    try {
      const response = await getApi('my-team');
      if (response.data?.status === true || response.status === true) {
        setLevelData(response.data.data || response.data);
      } else {
        setLevelData([]);
      }
      setTableview('levelTeam');
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch Root Tree Data from 'my-tree' API
  const fetchRootTreeData = async (targetUserId = null) => {
    setTreeLoading(true);
    setExpandedNodes({});
    try {
      const payload = targetUserId ? { user_id: String(targetUserId) } : {};
      const response = await postApi('my-tree', payload);

      if (response.data?.status === true || response.status === true) {
        const data = response.data?.data || response.data;
        setRootTree(data);
        if (data?.user) {
          setExpandedNodes({
            root: {
              left_child: data.left_child,
              right_child: data.right_child,
              isExpanded: true,
            },
          });
        }
      } else {
        setRootTree(null);
      }
    } catch (error) {
      console.error('Error fetching root tree data:', error);
      setRootTree(null);
    } finally {
      setTreeLoading(false);
    }
  };

  const getTreeData = async () => {
    setTableview('treeTeam');
    if (!rootTree) {
      await fetchRootTreeData(null);
    }
  };

  // Hold / Long-press handling
  const handleStartHold = (userObj) => {
    if (!userObj) return;
    isLongPressRef.current = false;
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    holdTimeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      setSelectedUserModal(userObj);
    }, 450);
  };

  const handleCancelHold = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  };

  // Toggle Node Expansion (Extend Tree UI)
  const toggleNodeExpand = async (userObj, pathKey) => {
    if (!userObj || !userObj.user_id) return;

    if (expandedNodes[pathKey]) {
      setExpandedNodes((prev) => ({
        ...prev,
        [pathKey]: {
          ...prev[pathKey],
          isExpanded: !prev[pathKey].isExpanded,
        },
      }));
      return;
    }

    setLoadingNodes((prev) => ({ ...prev, [pathKey]: true }));
    try {
      const response = await postApi('my-tree', { user_id: String(userObj.user_id) });
      if (response.data?.status === true || response.status === true) {
        const data = response.data?.data || response.data;
        setExpandedNodes((prev) => ({
          ...prev,
          [pathKey]: {
            left_child: data.left_child,
            right_child: data.right_child,
            isExpanded: true,
          },
        }));
      }
    } catch (error) {
      console.error('Error expanding tree node:', error);
    } finally {
      setLoadingNodes((prev) => ({ ...prev, [pathKey]: false }));
    }
  };

  const handleNodeClick = (e, userObj, pathKey) => {
    if (isLongPressRef.current) {
      isLongPressRef.current = false;
      return;
    }
    toggleNodeExpand(userObj, pathKey);
  };

  const handleSearchUser = (e) => {
    e.preventDefault();
    if (searchUserId.trim()) {
      fetchRootTreeData(searchUserId.trim());
      setSearchUserId('');
    }
  };

  // Recursive Tree Node Renderer
  const renderTreeNode = (userObj, position = 'root', pathKey = 'root') => {
    if (!userObj) {
      return (
        <div className="tree-node empty d-inline-block">
          <i className="bi bi-dash-circle text-muted" style={{ fontSize: '1rem', display: 'block', marginBottom: 2 }}></i>
          <div className="tn-name text-muted" style={{ fontSize: '0.75rem' }}>Empty</div>
          <div className="tn-id" style={{ fontSize: '0.68rem', opacity: 0.5 }}>Available</div>
        </div>
      );
    }

    const isExpanded = expandedNodes[pathKey]?.isExpanded;
    const isLoading = loadingNodes[pathKey];
    const childrenData = expandedNodes[pathKey];

    return (
      <div className="tree-node-wrapper d-inline-flex flex-column align-items-center my-2">
        <div
          className={`tree-node ${position}`}
          onMouseDown={() => handleStartHold(userObj)}
          onMouseUp={handleCancelHold}
          onMouseLeave={handleCancelHold}
          onTouchStart={() => handleStartHold(userObj)}
          onTouchEnd={handleCancelHold}
          onClick={(e) => handleNodeClick(e, userObj, pathKey)}
        >
          <div className="d-flex align-items-center justify-content-between">
            <span className="tn-badge">{position.toUpperCase()}</span>
            <button
              type="button"
              className="tn-info-btn"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUserModal(userObj);
              }}
              title="View User Details"
            >
              <i className="bi bi-info-circle"></i>
            </button>
          </div>

          <div className="tn-avatar">
            <i className="bi bi-person-fill"></i>
          </div>

          <div className="tn-name">{userObj.name || 'User'}</div>
          <div className="tn-id">ID: {userObj.user_id}</div>
          <div className="tn-stake">${Number(userObj.my_stake || 0).toLocaleString()}</div>

          <div className="tn-expand-btn">
            {isLoading ? (
              <span className="spinner-border spinner-border-sm text-success"></span>
            ) : (
              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            )}
          </div>
        </div>

        {/* Extended Sub-Tree Branches */}
        {isExpanded && childrenData && (
          <div className="w-100 mt-2">
            <div className="tree-line-v"></div>
            <div className="tree-line-h-wrap"></div>

            <div className="row g-2 justify-content-center flex-nowrap" style={{ minWidth: 260 }}>
              <div className="col-6 text-center">
                {renderTreeNode(childrenData.left_child, 'left', `${pathKey}-left`)}
              </div>
              <div className="col-6 text-center">
                {renderTreeNode(childrenData.right_child, 'right', `${pathKey}-right`)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'directTeam', label: 'Direct Team', icon: 'bi-people', action: getTeamdata },
    // { id: 'levelTeam', label: 'Level Team', icon: 'bi-diagram-3', action: getlevelData },
    { id: 'treeTeam', label: 'Tree View', icon: 'bi-share', action: getTreeData },
  ];

  const statsCards = [
    { label: 'Direct Referrals', value: myteam?.direct_referral || 0, icon: 'bi-person-plus', color: 'green' },
    { label: 'Total Team', value: myteam?.total_team || 0, icon: 'bi-people', color: 'blue' },
    { label: 'Left Users', value: myteam?.left_team || 0, icon: 'bi-arrow-left-circle', color: 'yellow' },
    { label: 'Right Users', value: myteam?.right_team || 0, icon: 'bi-arrow-right-circle', color: 'purple' },
  ];

  return (
    <div className="page-wrapper">
      {/* Header */}
      <div className="page-header mb-4">
        <div className="section-label">Network</div>
        <h2 className="page-title">My Team</h2>
        <p className="page-subtitle">Monitor and grow your referral network</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        {statsCards.map((card, i) => (
          <div key={i} className="col-md-3 col-6">
            {loading ? (
              <div className="skeleton" style={{ height: 90, borderRadius: 18 }}></div>
            ) : (
              <div className={`metric-card ${card.color}`}>
                <div className={`metric-icon ${card.color}`}>
                  <i className={`bi ${card.icon}`}></i>
                </div>
                <div>
                  <div className="metric-label">{card.label}</div>
                  <div className="metric-value">{card.value}</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Binary Balance Bar */}
      {!loading && (myteam?.left_team > 0 || myteam?.right_team > 0) && (
        <div className="p-card mb-4">
          <p className="section-label">Binary Balance</p>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="p-progress-wrap">
                <div className="p-progress-header">
                  <span className="p-progress-label">Left Branch</span>
                  <span className="p-progress-value">{myteam?.left_team || 0} members</span>
                </div>
                <div className="p-progress">
                  <div
                    className="p-progress-bar"
                    style={{
                      width: `${Math.min(((myteam?.left_team || 0) / Math.max(myteam?.total_team || 1, 1)) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="p-progress-wrap">
                <div className="p-progress-header">
                  <span className="p-progress-label">Right Branch</span>
                  <span className="p-progress-value">{myteam?.right_team || 0} members</span>
                </div>
                <div className="p-progress">
                  <div
                    className="p-progress-bar"
                    style={{
                      width: `${Math.min(((myteam?.right_team || 0) / Math.max(myteam?.total_team || 1, 1)) * 100, 100)}%`,
                      background: 'linear-gradient(90deg, var(--page-blue), var(--page-purple))',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs + Content */}
      <div className="p-card">
        {/* Tab Buttons */}
        <div className="p-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`p-tab ${tabaleView === tab.id ? 'active' : ''}`}
              onClick={tab.action}
            >
              <i className={`bi ${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        {tableLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success mb-2"></div>
            <p style={{ color: 'var(--text-secondary)' }}>Loading team data...</p>
          </div>
        ) : (
          <>
            {/* Direct Team */}
            {tabaleView === 'directTeam' && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 style={{ margin: 0, fontWeight: 700 }}>Direct Referrals</h5>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {myteam?.list?.length || 0} members
                  </span>
                </div>
                {!myteam?.list?.length ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-person-plus"></i>
                    </div>
                    <h6>No Direct Referrals Yet</h6>
                    <p>Share your referral code to grow your team</p>
                  </div>
                ) : (
                  <div className="p-table-wrap">
                    <table className="p-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User Code</th>
                          <th>Stake Status</th>
                          <th>Join Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myteam.list.map((item, i) => (
                          <tr key={item.id}>
                            <td className="dim">{i + 1}</td>
                            <td>
                              <span className="u-code">
                                {item.my_code?.slice(0, 6)}••••{item.my_code?.slice(-4)}
                              </span>
                            </td>
                            <td>
                              <span className={`s-pill ${item.stake_status === 'Active' ? 'active' : 'inactive'}`}>
                                {item.stake_status || 'Inactive'}
                              </span>
                            </td>
                            <td className="dim" style={{ fontSize: '0.82rem' }}>
                              {new Date(item.created_at).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Level Team */}
            {tabaleView === 'levelTeam' && (
              <>
                <h5 style={{ marginBottom: '1rem', fontWeight: 700 }}>Level Distribution</h5>
                {!levelData?.levelCounts || Object.keys(levelData.levelCounts).length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-diagram-3"></i>
                    </div>
                    <h6>No Level Data</h6>
                    <p>Grow your team to unlock level rewards</p>
                  </div>
                ) : (
                  <div className="row g-2">
                    {Object.entries(levelData.levelCounts).map(([level, count]) => (
                      <div key={level} className="col-md-4 col-6">
                        <div
                          style={{
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: 14,
                            padding: '1rem',
                            border: '1px solid rgba(255,255,255,0.07)',
                            textAlign: 'center',
                            transition: 'all 0.2s',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '0.72rem',
                              color: 'var(--text-secondary)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              fontWeight: 700,
                              marginBottom: 4,
                            }}
                          >
                            Level {level}
                          </div>
                          <div
                            style={{
                              fontSize: '1.6rem',
                              fontWeight: 800,
                              fontFamily: 'JetBrains Mono, monospace',
                              color: 'var(--page-accent)',
                            }}
                          >
                            {count}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>members</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Tree View */}
            {tabaleView === 'treeTeam' && (
              <div className="tree-container">
                {/* Header & Controls */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                  <h5 style={{ margin: 0, fontWeight: 700 }}>Network Binary Tree</h5>
                  <div className="d-flex align-items-center gap-2">
                    <form onSubmit={handleSearchUser} className="d-flex gap-2">
                      <input
                        type="text"
                        className="p-input"
                        placeholder="Search User ID..."
                        value={searchUserId}
                        onChange={(e) => setSearchUserId(e.target.value)}
                        style={{ padding: '0.45rem 0.85rem', fontSize: '0.82rem', width: 150 }}
                      />
                      <button type="submit" className="copy-btn" title="Search user tree">
                        <i className="bi bi-search"></i>
                      </button>
                    </form>
                    <button
                      className="copy-btn"
                      onClick={() => fetchRootTreeData(null)}
                      title="Reset to My Tree"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>
                      Reset Tree
                    </button>
                  </div>
                </div>

                {/* Tree Visual Content */}
                {treeLoading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-success mb-3"
                      style={{ width: '2.5rem', height: '2.5rem' }}
                    ></div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      Loading binary tree...
                    </p>
                  </div>
                ) : !rootTree || !rootTree.user ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="bi bi-share"></i>
                    </div>
                    <h6>No Tree Data Found</h6>
                    <p>Could not retrieve binary tree for the requested user.</p>
                    <button className="copy-btn mt-3" onClick={() => fetchRootTreeData(null)}>
                      Return to My Tree
                    </button>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', padding: '1rem 0' }} className="text-center">
                    {renderTreeNode(rootTree.user, 'root', 'root')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {selectedUserModal && (
        <div className="p-modal-overlay" onClick={() => setSelectedUserModal(null)}>
          <div className="p-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="p-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="user-avatar-lg">
                  <i className="bi bi-person-fill"></i>
                </div>
                <div>
                  <h5 className="modal-title mb-0" style={{ fontWeight: 700 }}>{selectedUserModal.name || 'User'}</h5>
                  <span className="user-id-badge">ID: {selectedUserModal.user_id}</span>
                </div>
              </div>
              <button className="btn-close-modal" onClick={() => setSelectedUserModal(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="p-modal-body">
              <div className="info-grid mb-3">
                <div className="info-box full-width">
                  <span className="info-label">Referral Code</span>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="info-val mono text-accent">{selectedUserModal.my_code || 'N/A'}</span>
                    {selectedUserModal.my_code && (
                      <button
                        className="copy-btn"
                        onClick={() => navigator.clipboard?.writeText(selectedUserModal.my_code)}
                        title="Copy Code"
                      >
                        <i className="bi bi-clipboard me-1"></i> Copy
                      </button>
                    )}
                  </div>
                </div>

                <div className="info-box">
                  <span className="info-label">Stake Amount</span>
                  <span className="info-val text-accent">${Number(selectedUserModal.my_stake || 0).toLocaleString()}</span>
                </div>

                <div className="info-box">
                  <span className="info-label">Team Business</span>
                  <span className="info-val text-blue">${Number(selectedUserModal.team_business || 0).toLocaleString()}</span>
                </div>

                <div className="info-box">
                  <span className="info-label">Left Network</span>
                  <span className="info-val text-warning">{selectedUserModal.left_users || 0} members</span>
                </div>

                <div className="info-box">
                  <span className="info-label">Right Network</span>
                  <span className="info-val text-purple">{selectedUserModal.right_users || 0} members</span>
                </div>
              </div>
            </div>

            <div className="p-modal-footer">
              <button className="copy-btn w-100 py-2" onClick={() => setSelectedUserModal(null)}>
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;